import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useParams } from 'react-router-dom';

import CustomDropdown from '@components/CustomDropdown';
import LoaderComponent from '@components/LoaderComponent';
import { toastError, toastSuccess } from '@components/Toast';

import {
	useAddSupplierOnboardingCostingMutation,
	useDeleteSupplierOnboardingCostingDocumentMutation,
	useDeleteSupplierOnboardingCostingMutation,
	useGetSupplierOnboardingCostingQuery,
	useUpdateSupplierOnboardingCostingMutation
} from '@features/supplier/api/supplierOnboardingCostingApi';

import { getFabricTypes } from '@store/design/action';
import { getAllFabricTypes } from '@store/reducers';

import { changeDateFormat, printApiError } from '@utils/Util';

const EXCEL_EXTENSIONS = ['xlsx', 'xls'];
const DOCUMENT_TYPE = 'SUPPLIER_ONBOARDING_COSTING';
const EMPTY_ARRAY = [];

const createEmptyRow = () => ({
	rowId: `row_${Date.now()}_${Math.random().toString(36).slice(2)}`,
	id: null,
	fabricType: null,
	departmentName: '',
	document: null,
	remarks: '',
	updatedBy: ''
});

const getFabricTypeCode = (fabricType) =>
	typeof fabricType === 'object' && fabricType !== null
		? (fabricType.constant ?? fabricType.code ?? fabricType.value)
		: fabricType;

const buildUpdatedBy = (apiRow) => {
	const name = apiRow?.updatedByName || apiRow?.modifiedByName || apiRow?.createdByName || '';
	const rawDate = apiRow?.updatedAt || apiRow?.modifiedAt || apiRow?.createdAt;
	const date =
		rawDate && changeDateFormat(rawDate, 'YYYY-MM-DD', 'DD-MMM-YYYY') !== 'Invalid date'
			? changeDateFormat(rawDate, 'YYYY-MM-DD', 'DD-MMM-YYYY')
			: '';
	return [name, date].filter(Boolean).join(', ');
};

const fileToBase64 = (file) =>
	new Promise((resolve, reject) => {
		const reader = new FileReader();
		reader.onload = () => resolve(reader.result);
		reader.onerror = reject;
		reader.readAsDataURL(file);
	});

const FactoryOnboardingCosting = () => {
	const { id } = useParams();
	const dispatch = useDispatch();
	const allFabricTypes = useSelector(getAllFabricTypes) || EMPTY_ARRAY;
	const [rows, setRows] = useState([createEmptyRow()]);

	useEffect(() => {
		if (!allFabricTypes.length) {
			dispatch(getFabricTypes());
		}
	}, []);

	const { data: costingData, isFetching } = useGetSupplierOnboardingCostingQuery(id, { skip: !id });
	const [addOnboardingCosting, { isLoading: isAdding }] = useAddSupplierOnboardingCostingMutation();
	const [updateOnboardingCosting, { isLoading: isUpdating }] = useUpdateSupplierOnboardingCostingMutation();
	const [deleteOnboardingCosting, { isLoading: isDeleting }] = useDeleteSupplierOnboardingCostingMutation();
	const [deleteOnboardingCostingDocument, { isLoading: isDeletingDoc }] =
		useDeleteSupplierOnboardingCostingDocumentMutation();

	const departmentItems = allFabricTypes.map((item) => ({ id: item.code, value: item.value }));

	const getFabricTypeName = (code) => allFabricTypes.find((item) => item.code === code)?.value || '';

	const mapApiRow = (apiRow, rowId) => {
		const fabricType = getFabricTypeCode(apiRow?.fabricType);
		return {
			rowId: rowId || `row_${apiRow?.id ?? Date.now()}`,
			id: apiRow?.id ?? null,
			fabricType: fabricType ?? null,
			departmentName: getFabricTypeName(fabricType) || apiRow?.fabricTypeName || '',
			document: apiRow?.documentResponse ?? apiRow?.document ?? null,
			remarks: apiRow?.remarks || '',
			updatedBy: buildUpdatedBy(apiRow)
		};
	};

	useEffect(() => {
		const list = Array.isArray(costingData)
			? costingData
			: (costingData?.data ?? costingData?.payload ?? []);
		setRows(list.length ? list.map((item) => mapApiRow(item)) : [createEmptyRow()]);
	}, [costingData, allFabricTypes.length]);

	const updateRow = (index, patch) => {
		setRows((prev) => prev.map((row, i) => (i === index ? { ...row, ...patch } : row)));
	};

	const saveRow = async (index, overrides = {}) => {
		const current = { ...rows[index], ...overrides };
		const body = {
			fabricType: current.fabricType || null,
			remarks: current.remarks || null
		};
		if (overrides.documentDTO) {
			body.documentDTO = overrides.documentDTO;
		}
		try {
			const response = current.id
				? await updateOnboardingCosting({ id: current.id, body }).unwrap()
				: await addOnboardingCosting({ supplierId: id, body }).unwrap();
			const apiRow = response?.payload ?? response?.data ?? response;
			setRows((prev) => prev.map((row, i) => (i === index ? mapApiRow(apiRow, row.rowId) : row)));
			toastSuccess(response?.message || 'Costing saved successfully');
		} catch (error) {
			toastError(error?.data?.message || 'Something went wrong');
		}
	};

	const handleDepartmentSelect = (index, obj) => {
		updateRow(index, { fabricType: obj.id, departmentName: obj.value });
		// New rows are only persisted once a costing file is uploaded.
		if (rows[index]?.id) {
			saveRow(index, { fabricType: obj.id });
		}
	};

	const handleFileUpload = async (index, e) => {
		const file = e.target.files?.[0];
		e.target.value = '';
		if (!file) return;
		const extension = file.name.split('.').pop()?.toLowerCase();
		if (!EXCEL_EXTENSIONS.includes(extension)) {
			toastError('Please upload an excel file only.');
			return;
		}
		try {
			const base64Str = await fileToBase64(file);
			const documentDTO = {
				name: file.name,
				docMimeType: file.type,
				documentType: DOCUMENT_TYPE,
				base64Str
			};
			await saveRow(index, { documentDTO });
		} catch (error) {
			printApiError(error);
		}
	};

	const removeFile = async (index) => {
		const row = rows[index];
		if (!row.id) {
			updateRow(index, { document: null });
			return;
		}
		try {
			const response = await deleteOnboardingCostingDocument(row.id).unwrap();
			updateRow(index, { document: null });
			toastSuccess(response?.message || 'Document removed successfully');
		} catch (error) {
			toastError(error?.data?.message || 'Something went wrong');
		}
	};

	const handleRemarksChange = (index, value) => {
		updateRow(index, { remarks: value });
	};

	const handleRemarksBlur = (index) => {
		const row = rows[index];
		// New rows are only persisted once a costing file is uploaded.
		if (!row.id) return;
		saveRow(index, {});
	};

	const handleAddRow = () => {
		setRows((prev) => [...prev, createEmptyRow()]);
	};

	const handleDeleteRow = async (index) => {
		const row = rows[index];
		if (!row.id) {
			setRows((prev) => (prev.length > 1 ? prev.filter((_, i) => i !== index) : [createEmptyRow()]));
			return;
		}
		try {
			const response = await deleteOnboardingCosting(row.id).unwrap();
			setRows((prev) => {
				const next = prev.filter((_, i) => i !== index);
				return next.length ? next : [createEmptyRow()];
			});
			toastSuccess(response?.message || 'Row deleted successfully');
		} catch (error) {
			toastError(error?.data?.message || 'Something went wrong');
		}
	};

	const isBusy = isFetching || isAdding || isUpdating || isDeleting || isDeletingDoc;

	return (
		<LoaderComponent loading={isBusy}>
			<div className="factory-onboarding-costing">
				<p className="gray_dark_02 regular-12 mb-2">Table</p>
				<table className="table with-border hover factory-onboarding-costing-table">
					<thead>
						<tr>
							<th className="weight-500 regular-12">Department</th>
							<th className="weight-500 regular-12">Upload summary costing</th>
							<th className="weight-500 regular-12">Remarks</th>
							<th className="weight-500 regular-12">Updated by</th>
							<th className="weight-500 regular-12">Action</th>
						</tr>
					</thead>
					<tbody>
						{rows.map((row, index) => (
							<tr key={row.rowId}>
								<td className="department-column">
									<CustomDropdown
										title={'Select'}
										items={departmentItems}
										selectedItem={row.departmentName || ''}
										onItemClick={(obj) => handleDepartmentSelect(index, obj)}
									/>
								</td>
								<td className="upload-column">
									{row.document ? (
										<span className="file-chip d-inline-flex align-items-center">
											<span className="file-name regular-12 gray_dark_01">
												{row.document.name}
											</span>
											<span
												className="delete-icon cursor-pointer ml-2"
												onClick={() => removeFile(index)}
											>
												<img
													src={`${process.env.PUBLIC_URL}/icons/delete24.svg`}
													alt="delete"
												/>
											</span>
										</span>
									) : (
										<div className="upload-box text-center">
											<label
												htmlFor={`factory-costing-upload-${row.rowId}`}
												className="cursor-pointer m-0"
											>
												<img
													src={`${process.env.PUBLIC_URL}/icons/upload-gray.svg`}
													alt="upload"
												/>
											</label>
											<input
												type="file"
												className="d-none"
												id={`factory-costing-upload-${row.rowId}`}
												accept=".xlsx, .xls"
												onChange={(e) => handleFileUpload(index, e)}
											/>
										</div>
									)}
								</td>
								<td className="remarks-column">
									<textarea
										autoComplete="off"
										className="remarks-input"
										placeholder="Edit Cell"
										value={row.remarks}
										onChange={(e) => handleRemarksChange(index, e.target.value)}
										onBlur={() => handleRemarksBlur(index)}
									/>
								</td>
								<td className="updated-by-column">
									<span className="regular-14 gray_dark_02">{row.updatedBy}</span>
								</td>
								<td className="action-column">
									<div className="option">
										<div className="dropdown">
											<button
												className="btn btn-default dropdown-toggle"
												type="button"
												id={`factory-costing-action-${row.rowId}`}
												data-toggle="dropdown"
												aria-expanded="false"
											>
												<img
													src={`${process.env.PUBLIC_URL}/icons/more_icon.svg`}
													alt="action"
												/>
											</button>
											<ul
												className="dropdown-menu dropdown-menu-right shadow-lg"
												role="menu"
												aria-labelledby={`factory-costing-action-${row.rowId}`}
											>
												<li
													role="presentation"
													className="px-4 pb-3 pt-3"
													onClick={() => handleDeleteRow(index)}
												>
													<a
														role="menuitem"
														tabIndex={-1}
														className="font-weight-normal text-black"
													>
														Delete
													</a>
												</li>
											</ul>
										</div>
									</div>
								</td>
							</tr>
						))}
					</tbody>
				</table>
				<button className="button text add-row-button text-only" onClick={handleAddRow}>
					<img src={`${process.env.PUBLIC_URL}/icons/add-round.svg`} alt="add row" width={24} />
				</button>
			</div>
		</LoaderComponent>
	);
};

export default FactoryOnboardingCosting;
